import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Users, Building2, HandHeart, Target, Megaphone, ShieldCheck, MapPin } from "lucide-react";
import api from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { HERO_IMAGE, COMMUNITY_IMAGE, DAKAR_IMAGE } from "@/lib/constants";

const PILIERS = [
  { Icon: ShieldCheck, title: "Foi & éthique", text: "La foi comme boussole éthique et l'intégrité au cœur de l'action publique." },
  { Icon: Users, title: "La jeunesse", text: "Première richesse stratégique de la Nation et boussole de l'action publique." },
  { Icon: HandHeart, title: "L'État serviteur", text: "Exercer le pouvoir comme une charge au service du peuple et du bien commun." },
  { Icon: Target, title: "Autodétermination", text: "Maîtriser notre temps, nos priorités et notre voie de développement souveraine." },
];

const STATS = [
  { value: "14", label: "Régions couvertes" },
  { value: "120+", label: "Comités locaux" },
  { value: "25k+", label: "Sympathisants" },
  { value: "8", label: "Comités diaspora" },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/articles", { params: { limit: 3 } }).then(({ data }) => setArticles(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO description="DEFAR - Les Bâtisseurs propose une voie sénégalaise, enracinée dans notre foi, notre histoire et nos valeurs. Ensemble, bâtissons le Sénégal de demain." />
      {/* HERO */}
      <section className="relative bg-brand-blue overflow-hidden">
        <img src={HERO_IMAGE} alt="Sénégal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/95 via-brand-blue/85 to-brand-blue/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-36 lg:py-44">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="inline-block text-xs tracking-[0.25em] uppercase font-bold text-brand-accent border-l-2 border-brand-accent pl-3">
              Pour un Sénégal prospère, ancré dans ses valeurs
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
              Ensemble, bâtissons le Sénégal de demain
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-blue-100 leading-relaxed max-w-2xl">
              DEFAR - Les Bâtisseurs propose une voie sénégalaise, enracinée dans notre foi,
              notre histoire et nos valeurs, et résolument tournée vers l'avenir.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-rejoindre-btn"
                onClick={() => navigate("/adhesion")}
                className="inline-flex items-center justify-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-8 py-4 text-base font-semibold"
              >
                Rejoindre DEFAR <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-soutenir-btn"
                onClick={() => navigate("/adhesion?type=soutien")}
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-brand-blue transition-colors px-8 py-4 text-base font-semibold"
              >
                Soutenir le mouvement
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-white">{s.value}</div>
              <div className="mt-1 text-sm text-emerald-100">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PILIERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <SectionHeading
          overline="Nos valeurs"
          title="Les piliers du mouvement"
          subtitle="DEFAR repose sur des engagements forts qui guident chacune de nos actions sur le terrain."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILIERS.map(({ Icon, title, text }) => (
            <div key={title} className="bg-white border border-border p-7 rounded-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 flex items-center justify-center bg-brand-blue/5 text-brand-blue">
                <Icon size={24} />
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LE MOUVEMENT split */}
      <section className="bg-brand-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={COMMUNITY_IMAGE} alt="Communauté" className="w-full aspect-[4/3] object-cover rounded-sm" />
            <div className="absolute -bottom-6 -right-6 hidden sm:block bg-brand-green text-white p-6 rounded-sm max-w-[200px]">
              <Building2 size={28} />
              <p className="mt-2 text-sm font-semibold">Un mouvement ancré dans les territoires</p>
            </div>
          </div>
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-green">Qui sommes-nous</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              Un mouvement de citoyens engagés
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Né de la volonté de citoyennes et de citoyens déterminés, DEFAR - Les Bâtisseurs
              œuvre pour un Sénégal plus juste et plus prospère. Nous croyons en la force du
              collectif et en la capacité de chacun à contribuer au changement.
            </p>
            <ul className="mt-6 space-y-3">
              {["Écoute des populations dans toutes les régions", "Actions concrètes de solidarité sur le terrain", "Implication active de la diaspora"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-1.5 h-1.5 w-1.5 bg-brand-green shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/le-mouvement" className="mt-8 inline-flex items-center gap-2 text-brand-blue font-semibold hover:gap-3 transition-all">
              Découvrir le mouvement <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ACTUALITES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-12">
          <SectionHeading overline="Restez informés" title="Dernières actualités" />
          <Link to="/actualites" className="hidden sm:inline-flex items-center gap-2 text-brand-blue font-semibold shrink-0 pb-12">
            Toutes les actualités <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <ArticleCard key={a.id} article={a} testid={`home-article-${i}`} />
          ))}
        </div>
      </section>

      {/* CTA carte */}
      <section className="relative bg-brand-blue overflow-hidden">
        <img src={DAKAR_IMAGE} alt="Dakar" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-brand-blue/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Megaphone size={36} className="mx-auto text-brand-accent" />
          <h2 className="mt-5 text-3xl sm:text-4xl font-bold text-white">Trouvez le comité le plus proche</h2>
          <p className="mt-4 text-blue-100 max-w-2xl mx-auto">
            Le mouvement est présent dans toutes les régions du Sénégal et au sein de la diaspora.
          </p>
          <Link
            to="/comites"
            data-testid="home-comites-cta"
            className="mt-8 inline-flex items-center gap-2 bg-white text-brand-blue hover:bg-brand-green hover:text-white transition-colors px-8 py-4 font-semibold"
          >
            <MapPin size={18} /> Voir la carte des comités
          </Link>
        </div>
      </section>
    </div>
  );
}
